import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { MsgDeskPageProps } from '../../MsgDeskApp'
import { legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { currentLegacyDeskMode } from '@/msgdeskRuntime/deskNavigation'
import { selectSubRenderState, treeStore } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { editorOpenActionForMode } from '../../routing/editor'
import { MainAttachments } from '@/mailboxDesk/Page/components/Attachments/MainAttachments'
import { MenuSubsPanels, SubsToolbar } from '@/deskUiSkeletons/Page/Panels'
import { EnterPinForm, LoadingOverlay, QuickReply, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { MailboxMainMessage } from './MailboxMainMessage'
import { MailboxMenu } from './MailboxMenu'
import { AuthEntry } from '@/deskUiSkeletons/Shared/AuthEntry'
import { MailboxPanels } from './MailboxPanels'
import { MailboxEditorTemplate } from './editor/MailboxEditorTemplate'
import { MailboxRowSurfaceProvider } from './rowTemplates/RowSurface'
import { MailboxPanelsSurfaceProvider } from './Page/components/Panels/PanelsSurface'
import { MailboxSharedSurfaceProvider } from './Shared/SharedSurface'
import { MailboxEditorSurfaceProvider } from './editor/EditorSurface'
import { fetchMailboxSubPreview, type MailboxPreviewBody } from './mailboxPreviewLoader'
import {
  applyMailboxMode,
  isMailboxEditorPathname,
  mailboxModeFromPathname,
  pushMailboxEditorUrl,
  replaceMailboxModeUrl,
  type MailboxRouteMode,
} from '../mailboxRoutes'
import { mailboxEditorModeFromSegment, type MailboxEditorMode } from '../../routing/mailbox'

type WorkspaceKind = 'main' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceContract: Record<WorkspaceKind, LegacyContractName> = {
  main: 'workspace.main',
  attachments: 'workspace.attachments',
  favs: 'workspace.favs',
  trash: 'workspace.trash',
  drafts: 'workspace.drafts',
}

function modeTitle(mode: string) {
  if (mode === 'answers') return 'Inbox'
  if (mode === 'submsges') return 'Messages'
  if (mode === 'attachments') return 'Attachments'
  if (mode === 'favs') return 'Starred'
  if (mode === 'drafts') return 'Drafts'
  if (mode === 'trash') return 'Deleted'
  if (mode === 'editor') return 'Compose'
  return 'Reading pane'
}

function Workspace({ kind, active, children }: { kind: WorkspaceKind; active: boolean; children: ReactNode }) {
  return (
    <section
      {...legacyHookAttrs(workspaceContract[kind], `md-mailbox-workspace md-mailbox-workspace--${kind} min-h-0 min-w-0 flex-1 ${active ? 'flex' : 'hidden'} flex-col`)}
      style={{ display: active ? 'flex' : 'none' }}
    >
      {children}
    </section>
  )
}

export function MailboxMsgPage({
  isEditorOpen = false,
  editorMode,
  editorBridgeReady = false,
  initialMode = 'msg',
  initialEditorMode,
}: MsgDeskPageProps & { initialMode?: MailboxRouteMode; initialEditorMode?: MailboxEditorMode }) {
  const { state } = useSubsStore()
  const mode = state.renderProjection.mode
  const editorActive = isEditorOpen || mode === 'editor'
  const backgroundModeRef = useRef(mode === 'editor' ? 'msg' : mode)
  if (mode !== 'editor' && mode !== 'subs') backgroundModeRef.current = mode
  const backgroundMode = editorActive || mode === 'subs' ? backgroundModeRef.current : mode
  const mainActive = ['msg', 'answers', 'submsges', 'subs'].includes(backgroundMode)
  const routeTargetRef = useRef<HTMLDivElement | null>(null)
  const initialModeSettled = useRef(false)
  const initialModeAttempts = useRef(0)
  const [initialModeTick, setInitialModeTick] = useState(0)
  const [initialRouteReady, setInitialRouteReady] = useState(false)
  const initialEditorOpened = useRef(false)
  const editorWasOpen = useRef(false)
  const [menuCollapsed, setMenuCollapsed] = useState(false)
  const [previewCollapsed, setPreviewCollapsed] = useState(false)
  const [previewSubId, setPreviewSubId] = useState<number | null>(null)
  const [previewCache, setPreviewCache] = useState<Record<number, MailboxPreviewBody>>({})
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null)
  const [previewErrors, setPreviewErrors] = useState<Record<number, string>>({})
  const activePreviewBody = previewSubId ? previewCache[previewSubId] : undefined
  const activePreviewError = previewSubId ? previewErrors[previewSubId] : undefined
  const isPreviewLoading = previewSubId !== null && previewLoadingId === previewSubId

  useEffect(() => {
    if (isEditorOpen || initialModeSettled.current || initialMode === 'msg') {
      initialModeSettled.current = true
      setInitialRouteReady(true)
      return
    }

    // S1/H4 (docs/27 §12, docs/30 §7): the store mode is SEEDED to the route
    // mode for SSR, so it matches before legacy has actually navigated.
    // Settling must wait for the legacy bootstrap (editorBridgeReady) and then
    // consult LEGACY's real desk mode (the projection is held at the route
    // target during adoption). The attachments view is render-only — legacy
    // never enters an 'attachments' mode — so it keeps the store-mode check.
    if (!editorBridgeReady) return

    const currentState = treeStore.getState()
    if (currentState.subsLoading) return
    const matchesRoute = initialMode === 'attachments'
      ? currentState.renderProjection.mode === 'attachments'
      : currentLegacyDeskMode() === initialMode
    const timer = window.setTimeout(() => {
      if (matchesRoute) {
        initialModeSettled.current = true
        setInitialRouteReady(true)
        return
      }
      if (initialModeAttempts.current >= 100) return
      initialModeAttempts.current += 1
      applyMailboxMode(initialMode, routeTargetRef.current, { updateUrl: false })
      setInitialModeTick(value => value + 1)
    }, matchesRoute ? 300 : 100)

    return () => window.clearTimeout(timer)
  }, [editorBridgeReady, initialMode, initialModeTick, isEditorOpen])

  useEffect(() => {
    if (!initialEditorMode || !editorBridgeReady || !initialRouteReady || initialEditorOpened.current) return
    initialEditorOpened.current = true
    dispatchEditorAction({ action: editorOpenActionForMode(initialEditorMode) })
  }, [editorBridgeReady, initialEditorMode, initialRouteReady])

  useEffect(() => {
    if (isEditorOpen) {
      editorWasOpen.current = true
      if ((editorMode === 'new_msg' || editorMode === 'update_msg') && !isMailboxEditorPathname(window.location.pathname)) {
        pushMailboxEditorUrl(editorMode)
      }
      return
    }

    if (editorWasOpen.current && isMailboxEditorPathname(window.location.pathname)) {
      editorWasOpen.current = false
      replaceMailboxModeUrl('msg')
    }
  }, [editorMode, isEditorOpen])

  useEffect(() => {
    const handlePopState = () => {
      const segment = window.location.pathname.split('/').filter(Boolean).pop() || ''
      const routeEditorMode = mailboxEditorModeFromSegment(segment)
      if (routeEditorMode && editorBridgeReady) {
        dispatchEditorAction({ action: editorOpenActionForMode(routeEditorMode) })
        return
      }
      applyMailboxMode(mailboxModeFromPathname(window.location.pathname), routeTargetRef.current, { updateUrl: false })
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [editorBridgeReady])

  useEffect(() => {
    ;(window as any).__MSGDESK_MAILBOX_PREVIEW_OPEN__ = !previewCollapsed
    return () => {
      delete (window as any).__MSGDESK_MAILBOX_PREVIEW_OPEN__
    }
  }, [previewCollapsed])

  useEffect(() => {
    const handlePreviewSub = (event: Event) => {
      const elId = Number((event as CustomEvent<{ elId?: number }>).detail?.elId || 0)
      if (elId > 0) setPreviewSubId(elId)
    }

    window.addEventListener('msgdesk:mailbox-preview-sub', handlePreviewSub)
    return () => window.removeEventListener('msgdesk:mailbox-preview-sub', handlePreviewSub)
  }, [])

  useEffect(() => {
    if (!previewSubId || previewCollapsed || activePreviewBody) return

    const sub = selectSubRenderState(treeStore.getState(), previewSubId)
    if (!sub) return

    const controller = new AbortController()
    setPreviewLoadingId(previewSubId)
    setPreviewErrors(prev => {
      if (!prev[previewSubId]) return prev
      const next = { ...prev }
      delete next[previewSubId]
      return next
    })

    fetchMailboxSubPreview(sub, controller.signal)
      .then(preview => {
        setPreviewCache(prev => ({ ...prev, [preview.elId]: preview }))
      })
      .catch(error => {
        if (controller.signal.aborted) return
        setPreviewErrors(prev => ({
          ...prev,
          [previewSubId]: error instanceof Error ? error.message : String(error),
        }))
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoadingId(current => current === previewSubId ? null : current)
      })

    return () => controller.abort()
  }, [activePreviewBody, previewCollapsed, previewSubId])

  return (
    <MailboxSharedSurfaceProvider>
    <MailboxRowSurfaceProvider>
    <MailboxPanelsSurfaceProvider>
    <MailboxEditorSurfaceProvider>
      <ShellOverlays />
      <div {...legacyHookAttrs('page.root', 'md-mailbox-page fixed inset-0 z-[1] flex min-h-[620px] w-screen overflow-hidden bg-[#eef2f5] font-[-apple-system,BlinkMacSystemFont,"Segoe_UI",Arial,sans-serif] text-[#17212b]')} ref={routeTargetRef}>
        <MailboxMenu isEditorOpen={isEditorOpen} collapsed={menuCollapsed} onToggleCollapsed={() => setMenuCollapsed(value => !value)} />

        <div {...legacyHookAttrs('page.table', 'md-mailbox-shell flex min-w-0 flex-1 flex-col')}>
          <header {...legacyHookAttrs('page.header', 'flex h-14 flex-[0_0_56px] items-center border-b border-[#d9e1e8] bg-white px-5 shadow-[0_1px_0_rgba(23,33,43,0.03)] max-[760px]:px-3')}>
            <div {...legacyHookAttrs('page.headerCell', 'flex min-w-0 flex-1 items-center justify-between gap-4')}>
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold text-[#17212b]">{modeTitle(backgroundMode)}</div>
                <div className="truncate text-[11px] text-[#687684] max-[760px]:hidden">Msgdesk Mail</div>
              </div>
              <div className="flex items-center gap-2">
                {previewSubId ? <div className="rounded-full bg-[#eef4ff] px-2 py-1 text-[11px] font-medium text-[#1556a8] max-[760px]:hidden">Preview selected</div> : null}
                <div className="flex-none rounded-full bg-[#e8f3ea] px-2 py-1 text-[11px] font-medium text-[#27633a] max-[760px]:hidden">Online</div>
                {/* POSSESSION-MODEL: logged identity at the page top-right. */}
                <AuthEntry skin={{
                  root: 'flex items-center gap-1.5 text-[12px]',
                  signInButton: 'h-8 rounded-md border border-[#d4dde6] bg-white px-2.5 text-[12px] text-[#2563eb] hover:bg-[#edf2f6] whitespace-nowrap',
                  identity: 'flex items-center gap-1 whitespace-nowrap',
                  identityLink: 'text-[12px] font-semibold text-[#2563eb] hover:underline max-w-[140px] overflow-hidden text-ellipsis',
                  signOutButton: 'h-8 rounded-md px-2 text-[11px] text-[#687684] hover:bg-[#edf2f6]',
                }} />
              </div>
            </div>
          </header>

          <div className="flex min-h-0 flex-1">
            <div {...legacyHookAttrs('page.accentCell', 'w-1 flex-[0_0_4px] bg-[#2563eb]')} />
            <main {...legacyHookAttrs('page.workspaceCell', 'flex min-h-0 min-w-0 flex-1')}>
              <Workspace kind="main" active={mainActive}>
                <SubsToolbar />
                {backgroundMode === 'msg' ? (
                  <MailboxMainMessage
                    selectedSubId={previewSubId}
                    previewBody={activePreviewBody}
                    isPreviewLoading={isPreviewLoading}
                    previewError={activePreviewError}
                    isPreviewCollapsed={previewCollapsed}
                    onTogglePreview={() => setPreviewCollapsed(value => !value)}
                  />
                ) : (
                  <div className={previewCollapsed
                    ? 'grid min-h-0 flex-1 grid-cols-[minmax(320px,1fr)_44px] overflow-hidden max-[980px]:grid-cols-1 max-[980px]:grid-rows-[minmax(300px,46vh)_44px] max-[980px]:overflow-y-auto'
                    : 'grid min-h-0 flex-1 grid-cols-[minmax(340px,460px)_minmax(420px,1fr)] overflow-hidden max-[980px]:grid-cols-1 max-[980px]:grid-rows-[minmax(300px,46vh)_minmax(360px,1fr)] max-[980px]:overflow-y-auto'
                  }>
                    <div className="flex min-h-0 flex-col overflow-hidden border-r border-[#d9e1e8] max-[980px]:h-[46vh] max-[980px]:min-h-[300px] max-[980px]:border-b max-[980px]:border-r-0">
                      <MailboxPanels activeSection={backgroundMode === 'submsges' ? 1 : 0} />
                    </div>
                    <MailboxMainMessage
                      selectedSubId={previewSubId}
                      previewBody={activePreviewBody}
                      isPreviewLoading={isPreviewLoading}
                      previewError={activePreviewError}
                      isPreviewCollapsed={previewCollapsed}
                      onTogglePreview={() => setPreviewCollapsed(value => !value)}
                    />
                  </div>
                )}
              </Workspace>
              <section {...legacyHookAttrs('workspace.editor', '!hidden')} hidden aria-hidden="true" />
              <Workspace kind="attachments" active={backgroundMode === 'attachments'}><MainAttachments /></Workspace>
              <Workspace kind="favs" active={backgroundMode === 'favs'}><MenuSubsPanels mode="favs" /></Workspace>
              <Workspace kind="trash" active={backgroundMode === 'trash'}><MenuSubsPanels mode="trash" /></Workspace>
              <Workspace kind="drafts" active={backgroundMode === 'drafts'}><MenuSubsPanels mode="drafts" /></Workspace>
            </main>
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} />
          </div>

          <footer {...legacyHookAttrs('page.footer', 'flex h-6 flex-[0_0_24px] items-center justify-between bg-[#24313d] px-3 text-[10px] text-white')}>
            <span>Msgdesk Mail</span>
            <span>Connected</span>
          </footer>
        </div>
      </div>

      <MailboxEditorTemplate active={editorActive} editorMode={isEditorOpen ? editorMode : ''} />

      <QuickReply />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </MailboxEditorSurfaceProvider>
    </MailboxPanelsSurfaceProvider>
    </MailboxRowSurfaceProvider>
    </MailboxSharedSurfaceProvider>
  )
}
